import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Search, ClipboardList, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { OrderTicketCard } from "./OrderTicketCard";
import { OrderTicketDialog } from "./OrderTicketDialog";
import { STATUS_META } from "./orderStatus";
import { NotifyCustomerDialog } from "./NotifyCustomerDialog";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = [
  { key: "active", label: "All active" },
  { key: "open", label: "Open" },
  { key: "ordered", label: "Ordered" },
  { key: "arrived", label: "Arrived" },
  { key: "collected", label: "Collected / Closed" },
  { key: "overdue", label: "Overdue" },
];


export const CustomerOrdersTab = () => {
  const { user } = useAuth();
  const currentUser = () => user?.username || user?.name || "";
  const [data, setData] = useState({ orders: [], counts: {}, overdue: 0 });
  const [filter, setFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [notifying, setNotifying] = useState(null);

  const fetchOrders = useCallback(async () => {
    const params = { q: search.trim() || undefined };
    if (STATUS_META[filter]) params.status = filter;
    try {
      const res = await axios.get(`${API}/customer-orders`, { params });
      setData(res.data);
    } catch {
      toast.error("Failed to load customer orders");
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const t = setTimeout(fetchOrders, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchOrders, search]);

  const handleStatus = async (order, status) => {
    if (status === order.status) return;
    try {
      const res = await saveWithRetry(() => axios.post(`${API}/customer-orders/${order.id}/status`, { status, by: currentUser() }));
      toast.success(`${order.ticket_no} → ${STATUS_META[status].label}`);
      fetchOrders();
      if (status === "arrived") setNotifying(res.data);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to update status"));
    }
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Delete ticket ${order.ticket_no} for ${order.customer_name}?`)) return;
    try {
      await axios.delete(`${API}/customer-orders/${order.id}`);
      toast.success("Ticket deleted");
      fetchOrders();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to delete ticket"));
    }
  };

  const orders = filter === "overdue" ? data.orders.filter((o) => o.is_overdue) : data.orders;
  const countFor = (k) => (k === "active" ? (data.counts.open || 0) + (data.counts.ordered || 0) + (data.counts.arrived || 0) : k === "overdue" ? data.overdue : data.counts[k] || 0);

  return (
    <div className="p-6 space-y-5" data-testid="customer-orders-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold">Customer Orders</h1>
            <p className="text-sm text-gray-500">Direct orders you've placed for customers</p>
          </div>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-order-ticket-btn"><Plus className="h-4 w-4 mr-2" />New Ticket</Button>
      </div>

      {data.overdue > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700" data-testid="orders-overdue-banner">
          <AlertTriangle className="h-4 w-4" />{data.overdue} ticket{data.overdue !== 1 ? "s are" : " is"} past the expected date and not yet arrived.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`orders-filter-${f.key}`}
            className={`rounded-full border px-3 py-1 text-sm transition-colors ${filter === f.key ? "border-purple-600 bg-purple-600 text-white" : "bg-white hover:bg-gray-100"} ${f.key === "overdue" && countFor(f.key) > 0 && filter !== f.key ? "border-red-400 text-red-600" : ""}`}>
            {f.label} <span className="ml-1 opacity-75">{countFor(f.key)}</span>
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input className="pl-8" placeholder="Search customer, item, supplier, CO-#" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="orders-search-input" />
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500">Loading orders...</div>
      ) : orders.length === 0 ? (
        <Card className="py-12 text-center"><CardContent>
          <ClipboardList className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <p className="text-gray-500" data-testid="orders-empty">No tickets here. Tap "New Ticket" to log a customer order.</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2" data-testid="orders-list">
          {orders.map((o) => (
            <OrderTicketCard key={o.id} order={o} onEdit={(x) => { setEditing(x); setDialogOpen(true); }} onDelete={handleDelete} onStatus={handleStatus} onNotify={setNotifying} />
          ))}
        </div>
      )}

      <NotifyCustomerDialog order={notifying} onClose={() => setNotifying(null)} onLogged={() => { toast.success("Notification logged"); fetchOrders(); }} by={currentUser()} />
      <OrderTicketDialog open={dialogOpen} onOpenChange={setDialogOpen} ticket={editing} onSaved={fetchOrders} />
    </div>
  );
};
